import RadioInput from "../RadioInput";
import SelectInput from "../SelectInput";
import MultiSelectInput from "../MultiSelectInput";
import ContactVerifyInput from "../ContactVerifyInput";
import DocumentPreviewInput from "../DocumentPreviewInput";
import TextInput from "../TextInput";
import {
  SELLER_TYPE_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
  MSME_TYPE_OPTIONS,
} from "./staticOptions";
import {
  FSSAI_LICENSE_NUMBER_LENGTH,
  OWNERSHIP_TYPE_OPTIONS,
  SHIPPING_PINCODE_LENGTH,
} from "utils/constants/onboarding";
import { useCityOptions, useCategoryOptions } from "./useDynamicOptions";

// Shared extension lists — referenced inline below.
const IMAGES_AND_PDF = ["image/*", ".pdf"];
const ALL_DOCUMENT_TYPES = ["image/*", ".pdf", ".csv", ".xls", ".xlsx", ".doc", ".docx"];

// key → { Component, options | useOptions, hint, ...extraProps }. Adding a field is one line.
// hint: shown below the field when there is no error — guides the user on expected format.
const SPECIAL_BY_KEY = {
  // Step 1
  seller_type:        { Component: RadioInput,       options: SELLER_TYPE_OPTIONS },
  owner_ship_type_input: { Component: SelectInput,   options: OWNERSHIP_TYPE_OPTIONS },
  business_category:  { Component: SelectInput,      useOptions: useCategoryOptions },
  supply_cities:      { Component: MultiSelectInput, useOptions: useCityOptions },
  contact_phone:      { Component: ContactVerifyInput, channel: "phone", hint: "10-digit mobile number" },
  contact_email:      { Component: ContactVerifyInput, channel: "email", hint: "Business email address" },

  // Step 2 (inside TodoList primitives)
  account_type_input:           { Component: RadioInput, options: ACCOUNT_TYPE_OPTIONS },
  account_number_input:         { Component: TextInput, isPassword: true, allowReveal: false, hint: "8–20 digit bank account number" },
  confirm_account_number_input: { hint: "Re-enter account number to confirm" },
  ifsc_code_input:              { hint: "11-character code — e.g. HDFC0001234" },
  bank_address_input:           { multiline: true },

  // Step 3 — shipping_city auto-fills shipping_state via stateValue from the city option.
  shipping_city:      { Component: SelectInput, useOptions: useCityOptions, withStateAutoFill: true },
  shipping_pincode: {
    digitsOnly: true,
    exactLength: SHIPPING_PINCODE_LENGTH,
    hint: "6-digit postal code",
  },

  // KYC text fields
  pan_number_input:   { hint: "10-character PAN — e.g. ABCDE1234F" },
  gst_number_input:   { hint: "15-character GSTIN, or type NA if not registered" },
  msme_number:        { hint: "Format: UDYAM-XX-00-0000000" },
  msme_type:          { Component: RadioInput, options: MSME_TYPE_OPTIONS },
  fssai_license_number: {
    digitsOnly: true,
    exactLength: FSSAI_LICENSE_NUMBER_LENGTH,
    allowEmpty: true,
    hint: "14-digit FSSAI number (leave blank if not applicable)",
  },

  // KYC / identity documents — exact keys from DynamoDB flow config (images + PDF only)
  pan_document_upload:          { allowedExtensions: IMAGES_AND_PDF },
  gst_certificate_upload:       { allowedExtensions: IMAGES_AND_PDF },
  cancelled_cheque_upload:      { allowedExtensions: IMAGES_AND_PDF },
  fssai_document:               { allowedExtensions: IMAGES_AND_PDF },
  msme_certificate:             { allowedExtensions: IMAGES_AND_PDF },
  non_msme_declaration:         { allowedExtensions: IMAGES_AND_PDF },
  incorporation_document:       { allowedExtensions: IMAGES_AND_PDF },

  // Catalogue upload — all document types allowed (images, PDF, CSV, Excel, Word)
  product_catalogue:            { allowedExtensions: ALL_DOCUMENT_TYPES },

  // Agreement documents live in public/pdfs/. Add a new key + pdfPath + fileName to extend.
  code_of_conduct:    { Component: DocumentPreviewInput, pdfPath: "/pdfs/ZHPL_Business_Partner_COC.pdf",    fileName: "Code of Conduct",    allowedExtensions: IMAGES_AND_PDF },
  purchase_agreement: { Component: DocumentPreviewInput, pdfPath: "/pdfs/ZHPL_Purchase_Agreement.docx", fileName: "Purchase Agreement", allowedExtensions: IMAGES_AND_PDF },
};

export const getSpecialForKey = (key) => (key ? SPECIAL_BY_KEY[key] : null);
